<template>
  <div>
    <div
      class="dimension-card"
      :class="{ 'is-expanded': isExpanded }">
      <div class="flex flex-row justify-content-between align-items-stretch w-100 h-100">
        <div class="flex flex-column">
          <p
            v-if="isWildCard"
            class="badge badge-other">
            {{ unsteeredDimensionsMessage }}
          </p>
          <template v-else>
            <div>
              <p
                v-if="dimensionType === SelectableDimensionType.DEAL"
                v-tooltip="dimensionDeal?.name"
                class="deal-id badge badge-other">
                {{ dimensionValue }}
              </p>
              <p
                v-else
                v-tooltip="dimensionPublisher?.name ?? dimensionValue"
                class="text-truncate"
                style="max-width: 20rem">
                {{ dimensionPublisher?.name ?? dimensionValue }}
              </p>
            </div>
            <div>
              <img
                v-if="dimensionPublisher?.logoUrl"
                v-tooltip="dimensionType === SelectableDimensionType.DEAL ? dimensionPublisher?.name : null"
                :src="dimensionPublisher?.logoUrl"
                :alt="dimensionPublisher?.name"
                class="publisher-logo" />
              <span
                v-else
                class="badge badge-primary">
                {{ capitalize(dimensionPublisher?.name) }}
              </span>
            </div>
          </template>
        </div>
        <div class="dimension-controls">
          <slot name="controls" />
          <div
            v-if="canExpand"
            class="btn-icon"
            @click="isExpanded = !isExpanded">
            <component
              :is="isExpanded ? IconExpanded : IconExpand"
              class="icon" />
          </div>
          <div
            v-if="!isReadOnly && !isWildCard"
            class="d-flex flex-row"
            style="gap: 1.75rem">
            <deal-preview
              v-if="dimensionType === SelectableDimensionType.DEAL"
              :is-active="isActiveDeal(dimensionValue)"
              @active-deal-changed="onActiveDealChanged(dimensionValue)" />
            <button
              class="btn btn-no-text btn-xxs btn-info-hollow"
              @click="emits('selectDimensions', { values: [dimensionValue], isAdd: !isDimensionSelected })">
              <component
                :is="isDimensionSelected ? IconMinus : IconAdd2"
                class="icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <transition name="squish">
      <template v-if="canExpand && isExpanded">
        <div class="dimension-card my-0 expanded-card">
          <slot name="expanded" />
        </div>
      </template>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import DealPreview from '@/common/components/deals/DealPreview.vue';
  import { onDealPreviewChanged } from '@/common/components/deals/DealsHelper';
  import {
    IconAdd2,
    IconCollapsed2 as IconExpand,
    IconExpanded2 as IconExpanded,
    IconMinus,
  } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import { SelectableDimensionType } from '@/common/models/package/SpendSteering';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';

  //#region Props

  interface IProps {
    dimensionValue: string;
    dimensionType: string;
    suppliers: MediaOwner[];
    deal: Package;
    activeDeal?: Package;
    isReadOnly?: boolean;
    canExpand?: boolean;
  }

  const {
    dimensionValue,
    dimensionType,
    suppliers,
    deal,
    activeDeal,
    isReadOnly,
    canExpand = false,
  } = defineProps<IProps>();

  //#endregion

  //#region Data

  const isExpanded = ref(false);
  const i18n = useI18n();

  //#endregion

  //#region Emits

  const emits = defineEmits(['selectDimensions']);

  //#endregion

  //#region Computed Properties

  const isWildCard = computed(() => {
    return dimensionValue === '*';
  });

  const selectedDeals = computed(() => {
    return deal.packages;
  });

  const dimensionDeal = computed(() => {
    return getDeal(dimensionValue);
  });

  const selectedSteeringDimensions = computed(() => {
    return deal.spendSteering.spendSteeringDimensions;
  });

  const isDimensionSelected = computed(() => {
    return selectedSteeringDimensions.value.map((x) => x.dimensionValue).includes(dimensionValue);
  });

  const dimensionPublisher = computed(() => {
    if (dimensionType === SelectableDimensionType.SELLER) {
      return suppliers.find((mo) => mo.id === Number(dimensionValue));
    }
    const selectedDealMediaOwner = dimensionDeal.value.mediaOwnerIDs[0];
    return suppliers.find((mo) => mo.id === selectedDealMediaOwner);
  });

  const unsteeredDimensionsMessage = computed(() => {
    const dimensionTypeFormatted = capitalize(
      i18n.t(SelectableDimensionType.DEAL ? 'names.deal' : 'names.mediaOwner', { n: 2 }),
    );
    return capitalize(i18n.t('messages.dealSpendSteeringUnsteeredDimensions', [dimensionTypeFormatted]));
  });

  //#endregion

  //#region Methods

  function getDeal(value: string) {
    return dimensionType === SelectableDimensionType.SELLER
      ? null
      : selectedDeals.value.find((p) => p.dealID == String(value));
  }

  // Deal Preview Functionality
  function isActiveDeal(dimensionValue: string) {
    return dimensionValue === activeDeal?.dealID;
  }

  function onActiveDealChanged(value: string) {
    const newActiveDeal = getDeal(value);
    onDealPreviewChanged(newActiveDeal, activeDeal, (pkg: Package) => {
      GlobalEventBus.$emit(GlobalEvents.activeDealchanged, pkg);
    });
  }

  //#endregion
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .publisher-logo {
    max-width: 10rem;
    max-height: 2.5rem;
  }

  .dimension-card.expanded-card {
    border-radius: 0 0 0.4rem 0.4rem;
    border-top: none;
    height: auto;
  }

  .dimension-card.is-expanded {
    border-radius: 0.4rem 0.4rem 0 0;
  }

  .dimension-card {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 0.4rem;
    border: 1px solid #dee2e6;
    background-color: #f8f9fa;
    margin: 0 1rem;
    flex: 1;
    height: 7.5rem;

    &:nth-of-type(odd) {
      background-color: #ffffff;
    }
  }

  .deal-id {
    font-family: $monospace-font-family;
  }

  .dimension-controls {
    display: flex;
    align-content: center;
    align-items: center;
    gap: 4rem;
    margin-right: 1rem;

    .read-only-weight {
      width: 5rem;
      text-align: left;
      margin-left: 1rem;
    }

    @media (max-width: 1750px) {
      gap: 2rem;
    }
  }

  .expanded {
    border-top: 2px solid black;
  }
</style>
